import { AcquiringChannel, Application, Catalogue, CatalogueItem, PriceType } from '../../../types';
import { ApplicationAPI } from '../../../types/salesforce/api/application/application-api';
import {
  AddressTypeAPI,
  DeliveryAPI,
} from '../../../types/salesforce/api/application/delivery-api';
import { Delivery, Solution } from '../../../types/application/opportunity';
import { getCatalogueItemFromCatalogue } from '../../get-device-from-catalogue';
import { Contact } from '../../../types/application/contact';
import { ContactAPI } from '../../../types/salesforce/api/application/contact-api';
import { Address } from '../../../types/application/address';
import { AddressAPI } from '../../../types/salesforce/api/application/address-api';
import { AccountAPI } from '../../../types/salesforce/api/application/account-api';
import { getDeliveryDetails } from '../../../../order/helpers/get-delivery-details';
import { PageName as OrderPageName } from '../../../../order/pages/page-names';
import { PageName as OnboardingPageNAme } from '../../../../onboarding/pages/page-names';
import { StageAPI } from '../../../types/salesforce/api/application/stage-api';
import { StepNames } from './step-names';
import { Payment, PaymentStatus } from '../../../types/application/payment';
import { PaymentAPI } from '../../../types/salesforce/api/application/payment-api';
import { Business } from '../../../types/application/business';
import {
  Director,
  DirectorDetails,
  IdentityTypeDirectors,
} from '../../../types/application/director-details';
import {
  DirectorAPI,
  DirectorDetailsAPI,
  IdTypeAPI,
} from '../../../types/salesforce/api/application/director-details-api';
import { SOUTH_AFRICA } from '../../countries';
import { getBusinessTypeDTOFromCompanyType } from './business-types';
import { getDTOFromConsumerType } from './consumer-types';
import { BankDetailsApi } from '../../../types/salesforce/api/application/bank-details-api';
import {
  BankAccountType,
  BankDetails,
  RequiredBankDetailsType,
} from '../../../types/application/bank-details';
import { BankAccountHolderTypeAPI } from '../../../types/salesforce/api/application/bank-account-holder-type-api';
import { BusinessRegistrationTypeApi } from '../../../types/salesforce/api/application/business-registration-type-api';
import { BankAccountTypeAPI } from '../../../types/salesforce/api/application/bank-account-type-api';
import { BankAccountValidationStatus } from '../../../types/application/credit-vetting';
import { BavStatusApi } from '../../../types/salesforce/api/application/bav-status-api';
import { CardFee, TerminalType } from '../../../types/application/card-fee';
import {
  CardFeeAPI,
  TerminalTypeAPI,
} from '../../../types/salesforce/api/application/card-fee-api';
import { getAmountExcludingVAT2DP, getTotalPriceInRands } from '../../currency';
import { PageName as DeviceActivationPageName } from 'src/features/device-activation/pages/page-names';
import { DeliveryOptions } from '../../../types/application/delivery-options';
import { Environment } from 'src/Environment';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import { Collection } from 'src/features/common/types/application/collection';
import { groupAndFormatSerialNumbers } from 'src/features/device-activation/helpers/utils';

export function getApplicationAPIFromApplication(
  application: Application,
  catalogue: Catalogue,
): ApplicationAPI {
  const deliveryDetails = getDeliveryDetails(application, catalogue);

  const deliveriesV2 = getDeliveries(groupCartItemsByAddress(application) || [], application);

  const deliveries: DeliveryWrapper[] = [];
  if (deliveryDetails.defaultAddress) {
    deliveries.push({
      delivery: deliveryDetails.defaultAddress,
      isBusinessAddress: true,
    });
  }

  if (deliveryDetails.customAddresses) {
    deliveryDetails.customAddresses.forEach((delivery) =>
      deliveries.push({
        delivery: delivery,
        isBusinessAddress: false,
      }),
    );
  }

  const applicationApi: ApplicationAPI = {
    solutionType: application.solutionType,
    referenceNumber: application.referenceNumber,
    remotePayment: application.remotePayment,
    account: {
      ...getAccountAPI(application.contact, application.businessDetails),
      pricingTier: !Environment.current.disabledFeatures.applicationCardFees
        ? application.opportunity?.cardFees?.pricingTier
        : catalogue.cardFees.pricingTier,
    },
    contact: getContactAPI(application.contact),
    bankDetails: getBankDetailsAPI(
      application.bankDetails,
      application.creditVetting?.bankAccountValidationStatus,
    ),
    directors: getDirectorDetailsAPI(application.directors),
    deliveries: !Environment.current.disabledFeatures.cartV2
      ? deliveriesV2.flatMap(getDeliveryAPIV2(catalogue, application))
      : deliveries.flatMap(getDeliveryAPI(catalogue, application)),
    cardFees: getCardFeesAPIfromCardFees(
      !Environment.current.disabledFeatures.applicationCardFees
        ? application.opportunity?.cardFees?.fees
        : catalogue.cardFees.fees,
    ),
    productsOrdered: [],
    payment: getPaymentAPI(application.payment),
    stage: getStageAPI(application),
    salesDetails: {
      agent: application.salesDetails?.agent ?? '',
      organisation: application.salesDetails?.organisation ?? '',
    },
    applicationKilled: application?.applicationKilled,
    isPendingAppComplete: isPendingAppComplete(application),
  };

  return applicationApi;
}

const groupCartItemsByAddress = (application: Application) => {
  const collections = application.collections || [];
  const deliveries = application.deliveries || [];
  const addresses: { addressId: string; solutions: Array<Collection | AppDelivery> }[] = collections
    .concat(deliveries)
    .reduce((acc, item) => {
      if (item.quantity <= 0) {
        return acc; // Skip items with quantity of 0 or less
      }
      const existingAddress = acc.find((address) => address.addressId === item.addressId);
      const isFromCollections = collections.some(
        (collection) =>
          collection.productId === item.productId && collection.addressId === item.addressId,
      );
      if (existingAddress) {
        existingAddress.solutions.push({
          productId: item.productId,
          quantity: item.quantity,
          id: item.id,
          addressId: item.addressId,
          deliveryOption: isFromCollections ? DeliveryOptions.Stock : DeliveryOptions.Deliver,
        });
      } else {
        acc.push({
          addressId: item.addressId || '',
          solutions: [
            {
              productId: item.productId,
              quantity: item.quantity,
              addressId: item.addressId,
              id: item.id,
              deliveryOption: isFromCollections ? DeliveryOptions.Stock : DeliveryOptions.Deliver,
            },
          ],
        });
      }
      return acc;
    }, [] as { addressId: string; solutions: Array<Collection | AppDelivery> }[]);

  return addresses;
};

const useStockOnHand = (application: Application, solution?: Solution, deliveryOption?: string) => {
  if (deliveryOption) return deliveryOption;
  if (solution) {
    return solution.deliveryOption;
  }
  if (application.useStockOnHandDeliveryOption) {
    return DeliveryOptions.Stock;
  }
};

function isStockOnHand(application: Application, solution: Solution): boolean {
  return !!application.collections?.filter((delivery) => delivery.productId === solution.productId)
    .length;
}

const useStockOnHandV2 = (application: Application, solution: Solution) => {
  return isStockOnHand(application, solution) ? DeliveryOptions.Stock : DeliveryOptions.Deliver;
};

function getCardFeesAPIfromCardFees(cardFees?: CardFee[]): CardFeeAPI[] | undefined {
  if (cardFees) {
    return cardFees.map<CardFeeAPI>((cardFee) => {
      return {
        deviceId: cardFee.deviceId,
        cardProviderName: cardFee.cardProviderName,
        percentage: cardFee.percentage,
        terminalType:
          cardFee.terminalType === TerminalType.CARD_PRESENT
            ? TerminalTypeAPI.CARD_PRESENT
            : TerminalTypeAPI.CARD_NOT_PRESENT,
      } as CardFeeAPI;
    });
  }
}

const getCartSolutions = (products: AppDelivery[] | Collection[], application: Application) => {
  return products.map((product: AppDelivery | Collection) => {
    const solution = application.cart?.find((item) => item.productId === product?.productId);
    const machines = groupAndFormatSerialNumbers(application.cardMachines || []);
    const machine = machines.find((m) => m.productId === product.productId);

    return {
      productId: product.productId,
      acquiringChannel: solution?.acquiringChannel as AcquiringChannel,
      quantity: product.quantity,
      serialNumbers:
        product.deliveryOption === DeliveryOptions.Stock ? machine?.serialNumbers || [] : [],
      friendlyTerminalName:
        product.deliveryOption === DeliveryOptions.Stock
          ? machine?.friendlyTerminalNames || []
          : [],
      deliveryOption: product.deliveryOption,
      paymentMethodCode: solution?.paymentMethodCode,
    };
  });
};

const getDelivery = (
  currentAddress: { addressId: string; solutions: Array<Collection | AppDelivery> },
  application: Application,
): Delivery => {
  const address = application.addresses?.filter(
    (address) => address.id === currentAddress.addressId,
  )?.[0];

  return {
    address: address || {},
    receiverDetails: {
      managerName: `${application.contact?.name} ${application.contact?.surname}`,
      idNumber: application.contact?.idNumber,
      phone: application.contact?.mobileNumber,
      workPhone: application.contact?.mobileNumber,
      cellPhone: application.contact?.mobileNumber,
      tradingAs:
        application.businessDetails?.companyName || application.businessDetails?.registeredName,
    },
    solutions: getCartSolutions(currentAddress.solutions || [], application) || [],
  };
};

const getDeliveries = (
  deliveries: {
    addressId: string;
    solutions: (Collection | AppDelivery)[];
  }[],
  application: Application,
): DeliveryWrapper[] => {
  return deliveries.map((delivery) => ({
    delivery: getDelivery(delivery, application),
    isBusinessAddress: delivery.addressId === 'default',
  }));
};

const getDeliveryAPIV2 =
  (catalogue: Catalogue, application: Application) =>
  (delivery: DeliveryWrapper): DeliveryAPI[] => {
    const { solutions, deliveryOption, address, receiverDetails } = delivery.delivery;
    // 3PO some addresses won't have solutions
    const _solutions = solutions.filter((sol) => sol.productId);

    return _solutions.map((solution) => {
      const device = getCatalogueItemFromCatalogue(
        catalogue,
        solution.productId,
        solution.acquiringChannel,
      );

      if (!device) {
        throw new Error(
          `Integration revamp: getDeliveryAPIV2: unknown device encountered: product id: ${solution.productId}, acq channel: ${solution.acquiringChannel}`,
        );
      }
      // Solution from application state
      const appSolution = application.opportunity?.solutions?.find(
        (sol) =>
          sol.productId === solution.productId &&
          sol.acquiringChannel === solution.acquiringChannel,
      );

      return {
        address: {
          addressType: delivery.isBusinessAddress
            ? AddressTypeAPI.BUSINESS
            : AddressTypeAPI.OPERATIONAL,
          street: address.street,
          complexName: address.building,
          suburb: address.suburb,
          city: address.city,
          province: address.province,
          country: address.country,
          postalCode: address.postalCode,
        },
        price:
          getAmountExcludingVAT2DP(getTotalPriceInRands(device.price.price || 0)) *
          solution.quantity,
        delivery_option:
          useStockOnHandV2(application, solution) || deliveryOption || device.device.deliveryOption,
        paymentMethodCode: appSolution?.paymentMethodCode,
        recipient: {
          name: receiverDetails.managerName,
          tradingAs: receiverDetails.tradingAs,
          idNumber: receiverDetails.idNumber,
          phoneNumberWork: receiverDetails.workPhone,
          phoneNumberCell: receiverDetails.cellPhone,
          phoneNumber: receiverDetails.cellPhone,
        },
        order: {
          quantity: solution.quantity,
          deviceName: device.device.title,
          deviceId: device.device.id,
          acquiringChannel: getAcquiringChannelV2(
            !!application.brokerLogin,
            device,
            solution,
            application,
          ),
          recipient: {
            name: receiverDetails.managerName,
            tradingAs: receiverDetails.tradingAs,
            idNumber: receiverDetails.idNumber,
            phoneNumberWork: receiverDetails.workPhone,
            phoneNumberCell: receiverDetails.cellPhone,
            phoneNumber: receiverDetails.cellPhone,
          },
          secondaryAcquiringChannel: device.device.secondaryAcquiringChannel,
          isProductCategory: device.device.isProductCategory,
          serialNumbers:
            solution.deliveryOption === DeliveryOptions.Stock
              ? device.device.serialNumbers || []
              : [],
          friendlyTerminalName:
            solution.deliveryOption === DeliveryOptions.Stock
              ? device.device.friendlyTerminalName || []
              : [],
        },
      } as DeliveryAPI;
    });
  };

function getBankAccountTypeAPI(bankAcountType?: BankAccountType): BankAccountTypeAPI | undefined {
  if (bankAcountType !== undefined) {
    switch (bankAcountType) {
      case BankAccountType.CURRENT_CHEQUE:
        return BankAccountTypeAPI.CHEQUE_CURRENT;
      case BankAccountType.SAVINGS:
        return BankAccountTypeAPI.SAVINGS;
      default:
        throw new Error(`Integration revamp: unknown bank account type: ${bankAcountType}`);
    }
  }
}

function getBavStatusAPI(
  bankAccountValidationStatus?: BankAccountValidationStatus,
): BavStatusApi | undefined {
  if (bankAccountValidationStatus !== undefined) {
    switch (bankAccountValidationStatus) {
      case BankAccountValidationStatus.BANK_DETAILS_VALID:
        return BavStatusApi.VALID;
      case BankAccountValidationStatus.BANK_DETAILS_INVALID:
        return BavStatusApi.INVALID;
      default:
        throw new Error(
          `Integration revamp: unknown bank account validation status: ${bankAccountValidationStatus}`,
        );
    }
  }
}

function getBankDetailsAPI(
  bankDetails?: BankDetails,
  bankAccountValidationStatus?: BankAccountValidationStatus,
): BankDetailsApi | undefined {
  if (bankDetails) {
    switch (bankDetails.type) {
      case undefined:
      case RequiredBankDetailsType.NO_CREDIT_VETTING:
        return {
          branchName: bankDetails.branchName,
          branchCode: bankDetails.branchCode,
          bankName: bankDetails.bankName,
          accountType: getBankAccountTypeAPI(bankDetails.accountType),
          bankAccountNumber: bankDetails.accountNumber,
          bankAccountHolder: bankDetails.accountHolderFullName,
        } as BankDetailsApi;

      case RequiredBankDetailsType.PERSONAL_ACCOUNT_CREDIT_VETTING:
        return {
          branchName: bankDetails.branchName,
          branchCode: bankDetails.branchCode,
          bankName: bankDetails.bankName,
          accountType: getBankAccountTypeAPI(bankDetails.accountType),
          bankAccountNumber: bankDetails.accountNumber,
          bankAccountHolder: bankDetails.accountHolderFullName,
          bankAccountHolderType: BankAccountHolderTypeAPI.PERSONAL,
          bankAccountHolderIdNumber: bankDetails.accountHolderIdNumber,
          bankAccountOpenSince: bankDetails.bankAccountOpenSince,
          debitOrderDate: bankDetails.debitOrderDate, // TODO: create DebitOrderDateAPI type and convert
          bavStatus: getBavStatusAPI(bankAccountValidationStatus),
        } as BankDetailsApi;

      case RequiredBankDetailsType.BUSINESS_ACCOUNT_WITH_REG_NUMBER_CREDIT_VETTING:
        return {
          branchName: bankDetails.branchName,
          branchCode: bankDetails.branchCode,
          bankName: bankDetails.bankName,
          accountType: getBankAccountTypeAPI(bankDetails.accountType),
          bankAccountNumber: bankDetails.accountNumber,
          bankAccountHolder: bankDetails.accountHolderFullName,
          bankAccountHolderType: BankAccountHolderTypeAPI.BUSINESS,
          businessRegistrationType: BusinessRegistrationTypeApi.BUSINESS_REGISTRATION,
          bankAccountHolderIdNumber: bankDetails.accountHolderIdNumber,
          bankAccountOpenSince: bankDetails.bankAccountOpenSince,
          debitOrderDate: bankDetails.debitOrderDate, // TODO: create DebitOrderDateAPI type and convert
          bavStatus: getBavStatusAPI(bankAccountValidationStatus),
        } as BankDetailsApi;

      case RequiredBankDetailsType.BUSINESS_ACCOUNT_WITH_RSA_ID_NUMBER_CREDIT_VETTING:
        return {
          branchName: bankDetails.branchName,
          branchCode: bankDetails.branchCode,
          bankName: bankDetails.bankName,
          accountType: getBankAccountTypeAPI(bankDetails.accountType),
          bankAccountNumber: bankDetails.accountNumber,
          bankAccountHolder: bankDetails.accountHolderFullName,
          bankAccountHolderType: BankAccountHolderTypeAPI.BUSINESS,
          businessRegistrationType: BusinessRegistrationTypeApi.RSA_ID,
          bankAccountHolderIdNumber: bankDetails.accountHolderIdNumber,
          bankAccountOpenSince: bankDetails.bankAccountOpenSince,
          debitOrderDate: bankDetails.debitOrderDate, // TODO: create DebitOrderDateAPI type and convert
          bavStatus: getBavStatusAPI(bankAccountValidationStatus),
        } as BankDetailsApi;
    }
  }
}

function getDirectorAPI(director: Director): DirectorAPI | undefined {
  let idTypeAPI = IdTypeAPI.RSA_ID;

  switch (director.idType) {
    case IdentityTypeDirectors.RSA_ID:
      idTypeAPI = IdTypeAPI.RSA_ID;
      break;
    case IdentityTypeDirectors.PASSPORT:
      idTypeAPI = IdTypeAPI.PASSPORT;
      break;
    case IdentityTypeDirectors.REFUGEE:
      idTypeAPI = IdTypeAPI.REFUGEE;
      break;
    case IdentityTypeDirectors.ASYLUM_SEEKER:
      idTypeAPI = IdTypeAPI.ASYLUM_SEEKER;
      break;
    default:
      throw new Error(`Inegration revamp: unknown director id type: ${director.idType}`);
  }

  return {
    idType: idTypeAPI,
    title: director.title,
    initials: director.initials,
    firstName: director.firstName,
    surname: director.surname,
    idNumber: director.idNumber,
    passportNumber: director.passportNumber,
    dateOfBirth: director.dateOfBirth,
    gender: director.gender,
    passportExpiryDate: director.passportExpiryDate,
    countryOfOrigin: director.countryOfOrigin,
  } as DirectorAPI;
}

function getDirectorDetailsAPI(directorDetails?: DirectorDetails): DirectorDetailsAPI | undefined {
  if (directorDetails) {
    return {
      directors: directorDetails.directors.map(getDirectorAPI),
    } as DirectorDetailsAPI;
  }
}

function getPaymentAPI(payment?: Payment): PaymentAPI | undefined {
  if (payment) {
    return {
      orderNumber: payment.orderNumber,
      isPaymentSuccessful: payment.paymentStatus === PaymentStatus.PAYMENT_SUCCESSFUL,
    } as PaymentAPI;
  }
}

function getAccountAPI(contact?: Contact, businessDetails?: Business): AccountAPI | undefined {
  if (contact || businessDetails) {
    return {
      businessAddress: getAddressAPI(contact?.businessAddress),
      businessType:
        businessDetails?.companyType !== undefined
          ? getBusinessTypeDTOFromCompanyType(businessDetails?.companyType)
          : undefined,
      businessName: businessDetails?.registeredName,
      businessTradingAsName: businessDetails?.companyName,
      businessRegistrationNumber: businessDetails?.companyRegNumber,
      industry: businessDetails?.industry,
      natureOfBusiness: businessDetails?.natureOfBusiness,
      mcc: businessDetails?.mcc,
      sic: businessDetails?.sic,
      businessWebsiteURL: businessDetails?.businessWebsiteURL,
      isBusinessKnownByDifferentName: businessDetails?.diffNameToggle,
      isCreditVettingConsentGiven: businessDetails?.consentToggle,
      businessDateOfRegistration: businessDetails?.dateOfRegistration,
      monthlyTurnover: businessDetails?.estimatedAmount,
      isMonthlyCardTurnoverUnknown: businessDetails?.processAmountToggle,
    } as AccountAPI;
  }
}

function getContactAPI(contact?: Contact): ContactAPI | undefined {
  if (contact) {
    return {
      firstNames: contact.name,
      surname: contact.surname,
      emailAddress: contact.emailAddress,
      phoneNumber: contact.mobileNumber,
      idNumber: contact.idNumber,
      idType: contact.identityType,
      countryOfOrigin: contact.countryOfOrigin,
      dateOfBirth: contact.dateOfBirth,
      passportExpiryDate: contact.passportExpiryDate,
      gender: contact.gender,
      consumerType: getDTOFromConsumerType(contact.consumerType),
      title: contact.title,
      initials: contact.initials,
      maritalStatus: contact.maritalStatus,
      contactPhoto: contact.selfieImageBase64,
      contactPhotoName: contact.selfieImageFileName,
      isResidentialAddressSameAsBusinessAddress: contact.isResidentialAddressSameAsBusinessAddress,
      residentialAddress: getAddressAPI(contact.residentialAddress?.address),
      residentialAddressPeriodFrom: contact.residentialAddress?.timeAtAddress,
      residentialAddressType: contact.residentialAddress?.addressType,
    } as ContactAPI;
  }
}

function getAddressAPI(address?: Address): AddressAPI | undefined {
  if (address) {
    return {
      street: address.street,
      complexName: address.building,
      suburb: address.suburb,
      city: address.city,
      province: address.province,
      postalCode: address.postalCode,
      country: address.country || SOUTH_AFRICA,
    } as AddressAPI;
  }
}

function getStageAPI(state: Application): StageAPI {
  if (!state.steps) {
    throw new Error(`Integration revamp: application.steps not defined: ${state.steps}`);
  }

  const currentStep = state.steps[state.currentStepIndex || 0];

  switch (currentStep.pageName) {
    case DeviceActivationPageName.ASSIGN_NAME:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.ORDER_SUMMARY,
      };
    case OrderPageName.CUSTOMER_DETAILS:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.ORDER_SUMMARY,
      } as StageAPI;
    case OrderPageName.YOUR_DETAILS:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.ORDER_SUMMARY,
      } as StageAPI;
    case OrderPageName.PAY_ONLINE:
    case OrderPageName.PAY_STATUS:
      if (state.payment?.paymentStatus === PaymentStatus.PAYMENT_SUCCESSFUL) {
        return {
          stepName: StepNames.YOUR_BUSINESS,
          subStepName: StepNames.DASHBOARD,
        } as StageAPI;
      }

      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.PAYMENTS,
      } as StageAPI;
    case OrderPageName.ORDER_SUMMARY:
    case OrderPageName.PAY:
      if (state.brokerLogin && state.payment?.onlinePaymentRequired) {
        return {
          stepName: StepNames.YOUR_BUSINESS,
          subStepName: StepNames.PAYMENTS,
        } as StageAPI;
      }

      const paymentStatus = state.payment?.paymentStatus;
      if (
        paymentStatus !== PaymentStatus.PAYMENT_SUCCESSFUL &&
        paymentStatus !== PaymentStatus.NONE
      ) {
        return {
          stepName: StepNames.YOUR_BUSINESS,
          subStepName: StepNames.PAYMENTS,
        } as StageAPI;
      } else {
        return {
          stepName: StepNames.YOUR_BUSINESS,
          subStepName: StepNames.DASHBOARD,
        } as StageAPI;
      }
    case OnboardingPageNAme.BUSINESS_DETAILS:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.BUSINESS_DETAILS,
      } as StageAPI;
    case OnboardingPageNAme.DIRECTOR_DETAILS:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.BUSINESS_IDS,
      } as StageAPI;
    case OnboardingPageNAme.CONFIRM_ADDRESS:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.BUSINESS_ADDRESS,
      } as StageAPI;
    case OnboardingPageNAme.PERSONAL_DETAILS:
      return {
        stepName: StepNames.YOUR_BUSINESS,
        subStepName: StepNames.YOUR_DETAILS,
      } as StageAPI;
    case OnboardingPageNAme.BANK_DETAILS:
      return {
        stepName: StepNames.YOUR_DETAILS,
        subStepName: StepNames.BANKING_DETAILS,
      } as StageAPI;
    case OnboardingPageNAme.PERSONAL_SELFIE:
      return {
        stepName: StepNames.YOUR_DETAILS,
        subStepName: StepNames.YOUR_DETAILS_SELFIE,
      } as StageAPI;
    case OnboardingPageNAme.RES_ADDRESS:
      return {
        stepName: StepNames.YOUR_DETAILS,
        subStepName: StepNames.CUSTOMER_ADDRESS,
      } as StageAPI;
    case OnboardingPageNAme.UPLOAD_DOCUMENTS:
      return {
        stepName: StepNames.YOUR_DETAILS,
        subStepName: StepNames.UPLOAD_DOCUMENTS,
      } as StageAPI;
    default:
      throw new Error(`Integration revamp: step name not yet supported: ${currentStep.pageName}`);
  }
}

type DeliveryWrapper = {
  delivery: Delivery;
  isBusinessAddress: boolean;
};

const getDeliveryAPI =
  (catalogue: Catalogue, application: Application) =>
  (delivery: DeliveryWrapper): DeliveryAPI[] => {
    const { solutions, deliveryOption, address, receiverDetails } = delivery.delivery;
    const machines = groupAndFormatSerialNumbers(application.cardMachines || []);
    // 3PO some addresses won't have solutions
    const _solutions = solutions.filter((sol) => sol.productId);

    return _solutions.map((solution) => {
      let thirdPartySolution: Solution | undefined = undefined;
      const device = getCatalogueItemFromCatalogue(
        catalogue,
        solution.productId,
        solution.acquiringChannel,
      );

      if (application.brokerLogin) {
        thirdPartySolution = application.thirdPartyDefaultAddressSolutions?.find(
          (s) => s.productId === solution.productId,
        );
      }

      if (!device) {
        throw new Error(
          `Integration revamp: getDeliveryAPI: unknown device encountered: product id: ${solution.productId}, acq channel: ${solution.acquiringChannel}`,
        );
      }
      // Solution from application state
      const appSolution = application.opportunity?.solutions?.find(
        (sol) =>
          sol.productId === solution.productId &&
          sol.acquiringChannel === solution.acquiringChannel,
      );
      const machine = machines.find((m) => m.productId === solution.productId);

      return {
        address: {
          addressType: delivery.isBusinessAddress
            ? AddressTypeAPI.BUSINESS
            : AddressTypeAPI.OPERATIONAL,
          street: address.street,
          complexName: address.building,
          suburb: address.suburb,
          city: address.city,
          province: address.province,
          country: address.country,
          postalCode: address.postalCode,
        },
        price:
          getAmountExcludingVAT2DP(getTotalPriceInRands(device.price.price || 0)) *
          solution.quantity,
        delivery_option:
          useStockOnHand(application, thirdPartySolution, deliveryOption) ||
          deliveryOption ||
          device.device.deliveryOption,
        paymentMethodCode: appSolution?.paymentMethodCode,
        recipient: {
          name: receiverDetails.managerName,
          tradingAs: receiverDetails.tradingAs,
          idNumber: receiverDetails.idNumber,
          phoneNumberWork: receiverDetails.workPhone,
          phoneNumberCell: receiverDetails.cellPhone,
          phoneNumber: receiverDetails.cellPhone,
        },
        order: {
          quantity: solution.quantity,
          deviceName: device.device.title,
          deviceId: device.device.id,
          acquiringChannel: getAcquiringChannel(
            !!application.brokerLogin,
            device,
            thirdPartySolution,
            deliveryOption,
          ),
          recipient: {
            name: receiverDetails.managerName,
            tradingAs: receiverDetails.tradingAs,
            idNumber: receiverDetails.idNumber,
            phoneNumberWork: receiverDetails.workPhone,
            phoneNumberCell: receiverDetails.cellPhone,
            phoneNumber: receiverDetails.cellPhone,
          },
          secondaryAcquiringChannel: device.device.secondaryAcquiringChannel,
          isProductCategory: device.device.isProductCategory,
          serialNumbers: machine?.serialNumbers || device.device.serialNumbers,
          friendlyTerminalName:
            machine?.friendlyTerminalNames || device.device.friendlyTerminalName,
        },
      } as DeliveryAPI;
    });
  };

function isDeliverable(device: CatalogueItem) {
  return (
    device.device.deliveryOptions.includes(DeliveryOptions.Deliver) ||
    !device.device.deliveryOptions.length
  );
}

function getAcquiringChannelV2(
  brokerLogin: boolean,
  device: CatalogueItem,
  solution: Solution,
  application: Application,
) {
  if (brokerLogin && isStockOnHand(application, solution)) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : AcquiringChannel.buyInstoreFicaInstore;
  }

  if (brokerLogin && solution.deliveryOption !== DeliveryOptions.Stock && isDeliverable(device)) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : AcquiringChannel.buyOnlineFicaOnline;
  }

  if (brokerLogin) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : device?.device.deliveryOption === DeliveryOptions.Deliver
      ? AcquiringChannel.buyOnlineFicaOnline
      : AcquiringChannel.buyInstoreFicaInstore;
  }

  return device?.device.acquiringChannel;
}

function getAcquiringChannel(
  brokerLogin: boolean,
  device: CatalogueItem,
  solution?: Solution,
  deliveryOption?: string,
) {
  if (deliveryOption && deliveryOption === DeliveryOptions.Stock) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : AcquiringChannel.buyInstoreFicaInstore;
  }

  if (
    brokerLogin &&
    solution &&
    solution.deliveryOption !== DeliveryOptions.Stock &&
    isDeliverable(device)
  ) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : AcquiringChannel.buyOnlineFicaOnline;
  }

  if (brokerLogin && solution && solution.deliveryOption === DeliveryOptions.Stock) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : AcquiringChannel.buyInstoreFicaInstore;
  }

  if (brokerLogin) {
    return device?.price.priceType === PriceType.MONTHLY
      ? AcquiringChannel.rentOnlineFicaOnline
      : device?.device.deliveryOption === DeliveryOptions.Deliver
      ? AcquiringChannel.buyOnlineFicaOnline
      : AcquiringChannel.buyInstoreFicaInstore;
  }

  return device?.device.acquiringChannel;
}

function isPendingAppComplete(application: Application): boolean {
  if (!application.pendingApp?.isPending || !application.currentStepIndex || !application.steps) {
    return false;
  }

  const currentStep = application.steps[application.currentStepIndex].pageName;
  const lastPendingStep = application.steps
    .slice()
    .reverse()
    .find((step) => {
      return (
        application.pendingApp?.pageUpdates?.find(
          (pendingStep) => pendingStep.subStepName.toLowerCase() === step.pageName.toLowerCase(),
        ) !== undefined
      );
    })?.pageName;

  return !!currentStep && !!lastPendingStep && currentStep === lastPendingStep;
}
